import { useMemo } from "react";
import { useNewHireJourneyTasksQuery } from "features/Resources/Task/hooks";
import { INCOMPLETE_TASK_STATUSES, TaskStatus } from "features/Resources/Task/constants";
import { NewHireJourney } from "models/v2/newHireJourney";
import { User } from "features/User/models";
import { Task } from "features/Resources/Task/models";
import { RelationType } from "models/v2/networkRelation";
import { StakeholderItem } from "../types";

function computeLastReminderAt(tasks: Task[]): string | undefined {
  const reminderTimes = tasks
    .filter((t) => t.lastReminderAt)
    .map((t) => new Date(t.lastReminderAt as string).getTime());
  if (reminderTimes.length === 0) return undefined;
  return new Date(Math.max(...reminderTimes)).toISOString();
}

export function computeStakeholderItem(user: User, tasks: Task[], roles: RelationType[]): StakeholderItem {
  const incompleteTasks = tasks.filter((t) => INCOMPLETE_TASK_STATUSES.includes(t.status));
  const completedTasks = tasks.filter((t) => t.status === TaskStatus.COMPLETED);
  const overdueTasks = incompleteTasks.filter((t) => t.isOverdue);
  const urgentTasks = incompleteTasks.filter((t) => t.isUrgent && !t.isOverdue);
  const activeTasks = incompleteTasks.filter((t) => !t.isOverdue && !t.isUrgent);

  return {
    user,
    tasks: incompleteTasks,
    lastReminderAt: computeLastReminderAt(tasks),
    hasOverdueTasks: overdueTasks.length > 0,
    overdueCount: overdueTasks.length,
    urgentCount: urgentTasks.length,
    activeCount: activeTasks.length,
    completedCount: completedTasks.length,
    totalCount: tasks.length,
    roles,
  };
}

export function getUserRoles(userId: string, journey: NewHireJourney): RelationType[] {
  const roles: RelationType[] = [];
  if (journey.manager?.id === userId) roles.push("manager");
  if (journey.buddy?.id === userId) roles.push("buddy");
  if (journey.peopleTeamContact?.id === userId) roles.push("people_team_contact");
  if (journey.recruiter?.id === userId) roles.push("recruiter");
  if (journey.itTeamContact?.id === userId) roles.push("it_team_contact");
  if (journey.newHire?.id === userId) roles.push("new_hire");
  return roles;
}

interface UseStakeholderGroupsOptions {
  enabled?: boolean;
  excludeUserId?: string;
  /** When provided, only tasks with these IDs are included */
  filterTaskIds?: Set<number>;
}

interface UseStakeholderGroupsResult {
  /** The excluded user's stakeholder data (if excludeUserId was provided) */
  excludedUserStakeholder?: StakeholderItem;
  /** All stakeholders with open tasks (flat list, excludes the excluded user) */
  stakeholdersWithOpenTasks: StakeholderItem[];
  isLoading: boolean;
  isFetched: boolean;
}

export function useStakeholderGroups(
  newHireJourney: NewHireJourney,
  options: UseStakeholderGroupsOptions = {},
): UseStakeholderGroupsResult {
  const { enabled = true, excludeUserId, filterTaskIds } = options;

  const { data: tasks = [], isLoading, isFetched } = useNewHireJourneyTasksQuery(newHireJourney?.id, { enabled });

  const { excludedUserStakeholder, stakeholdersWithOpenTasks } = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return {
        excludedUserStakeholder: undefined,
        stakeholdersWithOpenTasks: [] as StakeholderItem[],
      };
    }

    const filteredTasks = filterTaskIds ? tasks.filter((t) => filterTaskIds.has(Number(t.id))) : tasks;

    const tasksByAssignee = new Map<string, { user: User; tasks: Task[] }>();

    filteredTasks.forEach((task) => {
      if (!task.assigneeId || !task.assignee) return;
      const existing = tasksByAssignee.get(task.assigneeId);
      if (existing) {
        existing.tasks.push(task);
      } else {
        tasksByAssignee.set(task.assigneeId, {
          user: task.assignee,
          tasks: [task],
        });
      }
    });

    const withOpenTasks: StakeholderItem[] = [];
    let excludedUser: StakeholderItem | undefined;

    tasksByAssignee.forEach(({ user, tasks: userTasks }) => {
      const roles = getUserRoles(user.id, newHireJourney);
      const item = computeStakeholderItem(user, userTasks, roles);

      if (excludeUserId && user.id === excludeUserId) {
        excludedUser = item;
        return;
      }

      if (item.tasks.length > 0) {
        withOpenTasks.push(item);
      }
    });

    return { excludedUserStakeholder: excludedUser, stakeholdersWithOpenTasks: withOpenTasks };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depend on specific IDs, not full object
  }, [
    tasks,
    newHireJourney?.id,
    newHireJourney?.manager?.id,
    newHireJourney?.buddy?.id,
    newHireJourney?.peopleTeamContact?.id,
    newHireJourney?.recruiter?.id,
    newHireJourney?.itTeamContact?.id,
    newHireJourney?.newHire?.id,
    excludeUserId,
    filterTaskIds,
  ]);

  return {
    excludedUserStakeholder,
    stakeholdersWithOpenTasks,
    isLoading,
    isFetched,
  };
}
